<template>
  <v-card flat class="px-6">
    <v-card-text>
      <v-alert
        type="info"
        text
        dense
        class="mb-3"
      >
        {{ $t('fleet.roads.roadRequirements') }}
      </v-alert>
      <v-row class="mb-3">
        <v-col cols="6" class="text-center">
          <h4 class="primary--text">{{ $t('vehicle.load') }}</h4>
        </v-col>
        <v-col cols="6" class="text-center">
          <h4 class="primary--text">{{ $t('vehicle.unload') }}</h4>
        </v-col>
      </v-row>

      <v-row v-for="(item, index) in sortedPoints" :key="index" class="mb-2">
        <!-- Kolumna załadunków -->
        <v-col cols="6">
          <v-card v-if="item.type === 0" outlined elevation="2" class="pa-3">
            <v-row>
              <v-col cols="10">
                <v-row>
                  <v-col class="pt-0">
                    <label>{{ $t('fleet.roads.pointTitle') }}</label>
                    <LocationInput
                      @removeItem="deletePoint(item)"
                      v-model="item.location"
                      validation
                      hideRange
                      clearable
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <DatePicker
                      v-model="item.dateTime"
                      :label="$t('fleet.roads.dateTitle')"
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col class="d-flex">
                    <div class="mr-2">
                      <v-btn
                        v-if="canMoveUp(item)"
                        color="primary"
                        depressed
                        small
                        @click="moveUp(item)"
                      >
                        <v-icon>uil uil-arrow-up</v-icon>
                      </v-btn>
                    </div>
                    <div>
                      <v-btn
                        v-if="canMoveDown(item)"
                        color="primary"
                        depressed
                        small
                        @click="moveDown(item)"
                      >
                        <v-icon>uil uil-arrow-down</v-icon>
                      </v-btn>
                    </div>
                  </v-col>
                </v-row>

                <!-- Expansion panel for additional fields -->
                <v-expansion-panels flat class="mt-3">
                  <v-expansion-panel>
                    <v-expansion-panel-header>
                      {{ $t('fleet.roads.details') }}
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <v-row>
                        <v-col cols="12">
                          <RichTextEditor
                            v-model="item.description"
                            :label="$t('fleet.roads.description')"
                          />
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col cols="6">
                          <v-checkbox
                            v-model="item.showTimeSliders"
                            :label="$t('fleet.roads.setHours')"
                            @change="onTimeCheckboxChange(item)"
                          />
                        </v-col>
                        <v-col cols="6">
                          <v-checkbox
                            v-model="item.fix"
                            :label="$t('fleet.roads.fix')"
                          />
                        </v-col>
                      </v-row>
                      <v-row v-if="item.showTimeSliders">
                        <v-col cols="12">
                          <HourInput
                            v-model="item.timeFrom"
                            :label="$t('fleet.roads.timeFrom')"
                            :error="getTimeFromError(item)"
                            :error-messages="getTimeFromErrorMessage(item)"
                            @input="item.dirtTime = true"
                          />
                        </v-col>
                      </v-row>
                      <v-row v-if="item.showTimeSliders">
                        <v-col cols="12">
                          <HourInput
                            v-model="item.timeTo"
                            :label="$t('fleet.roads.timeTo')"
                            :error="getTimeToError(item)"
                            :error-messages="getTimeToErrorMessage(item)"
                            @input="item.dirtTime = true"
                          />
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col cols="6">
                          <v-text-field
                            v-model.number="item.weight"
                            :label="$t('fleet.roads.weight')"
                            type="number"
                            step="0.01"
                          />
                        </v-col>
                        <v-col cols="6">
                          <v-text-field
                            v-model.number="item.loadMeters"
                            :label="$t('fleet.roads.loadMeters')"
                            type="number"
                            step="0.01"
                          />
                        </v-col>
                      </v-row>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-col>
              <v-col cols="2" class="d-flex flex-column align-center justify-center">
                <v-btn
                  color="primary"
                  depressed
                  @click="switchType(item)"
                  :title="$t('fleet.roads.moveToUnload')"
                  class="mb-2"
                >
                  <v-icon>uil uil-arrow-right</v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </v-card>
        </v-col>

        <!-- Kolumna rozładunków -->
        <v-col cols="6">
          <v-card v-if="item.type === 1" outlined elevation="2" class="pa-3">
            <v-row>
              <v-col cols="2" class="d-flex flex-column align-center justify-center">
                <v-btn
                  color="primary"
                  depressed
                  @click="switchType(item)"
                  :title="$t('fleet.roads.moveToLoad')"
                  class="mb-2"
                >
                  <v-icon>uil uil-arrow-left</v-icon>
                </v-btn>
              </v-col>
              <v-col cols="10">
                <v-row>
                  <v-col class="pt-0">
                    <label>{{ $t('fleet.roads.pointTitle') }}</label>
                    <LocationInput
                      @removeItem="deletePoint(item)"
                      v-model="item.location"
                      validation
                      hideRange
                      clearable
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <DatePicker
                      v-model="item.dateTime"
                      :label="$t('fleet.roads.dateTitle')"
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col class="d-flex">
                    <div class="mr-2">
                      <v-btn
                        v-if="canMoveUp(item)"
                        color="primary"
                        depressed
                        small
                        @click="moveUp(item)"
                      >
                        <v-icon>uil uil-arrow-up</v-icon>
                      </v-btn>
                    </div>
                    <div>
                      <v-btn
                        v-if="canMoveDown(item)"
                        color="primary"
                        depressed
                        small
                        @click="moveDown(item)"
                      >
                        <v-icon>uil uil-arrow-down</v-icon>
                      </v-btn>
                    </div>
                  </v-col>
                </v-row>

                <!-- Expansion panel for additional fields -->
                <v-expansion-panels flat class="mt-3">
                  <v-expansion-panel>
                    <v-expansion-panel-header>
                      {{ $t('fleet.roads.details') }}
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <v-row>
                        <v-col cols="12">
                          <RichTextEditor
                            v-model="item.description"
                            :label="$t('fleet.roads.description')"
                          />
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col cols="6">
                          <v-checkbox
                            v-model="item.showTimeSliders"
                            :label="$t('fleet.roads.setHours')"
                            @change="onTimeCheckboxChange(item)"
                          />
                        </v-col>
                        <v-col cols="6">
                          <v-checkbox
                            v-model="item.fix"
                            :label="$t('fleet.roads.fix')"
                          />
                        </v-col>
                      </v-row>
                      <v-row v-if="item.showTimeSliders">
                        <v-col cols="12">
                          <HourInput
                            v-model="item.timeFrom"
                            :label="$t('fleet.roads.timeFrom')"
                            :error="getTimeFromError(item)"
                            :error-messages="getTimeFromErrorMessage(item)"
                            @input="item.dirtTime = true"
                          />
                        </v-col>
                      </v-row>
                      <v-row v-if="item.showTimeSliders">
                        <v-col cols="12">
                          <HourInput
                            v-model="item.timeTo"
                            :label="$t('fleet.roads.timeTo')"
                            :error="getTimeToError(item)"
                            :error-messages="getTimeToErrorMessage(item)"
                            @input="item.dirtTime = true"
                          />
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col cols="6">
                          <v-text-field
                            v-model.number="item.weight"
                            :label="$t('fleet.roads.weight')"
                            type="number"
                            step="0.01"
                          />
                        </v-col>
                        <v-col cols="6">
                          <v-text-field
                            v-model.number="item.loadMeters"
                            :label="$t('fleet.roads.loadMeters')"
                            type="number"
                            step="0.01"
                          />
                        </v-col>
                      </v-row>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
      </v-row>

      <v-divider class="my-4"></v-divider>

      <v-row>
        <v-col cols="12">
          <v-btn :disabled="points.length >= 30" color="primary" depressed @click="addPoint">
            {{ $t('common.add') }}
          </v-btn>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import DatePicker from '@/components/forms/DatePicker'
import LocationInput from '@/components/forms/LocationInput'
import RichTextEditor from '@/components/forms/RichTextEditor'
import HourInput from '@/components/forms/HourInput'
import * as moment from 'moment'

export default {
  name: 'RouteTab',
  components: {
    LocationInput,
    DatePicker,
    RichTextEditor,
    HourInput
  },
  props: {
    value: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      points: [],
      isReordering: false
    }
  },
  computed: {
    sortedPoints () {
      return [...this.points].sort((a, b) => a.order - b.order)
    }
  },
  watch: {
    points: {
      handler (newPoints) {
        if (!this.isReordering) {
          this.reorderPointsByDate()
        }
        // Emit zmian do parenta
        this.emitPoints()
      },
      deep: true
    }
  },
  created () {
    // Inicjalizacja z wartości z parenta
    if (this.value && this.value.length > 0) {
      this.points = this.value.map(point => ({
        ...point,
        showTimeSliders: !!(point.timeFrom || point.timeTo),
        timeFrom: this.convertTimeSpanToMillis(point.timeFrom),
        timeTo: this.convertTimeSpanToMillis(point.timeTo),
        dirtTime: false
      }))
    } else {
      this.addPoint()
    }
  },
  methods: {
    emitPoints () {
      const pointsToEmit = this.points.map(point => ({
        ...point,
        timeFrom: this.convertMillisToTimeSpan(point.timeFrom),
        timeTo: this.convertMillisToTimeSpan(point.timeTo)
      }))
      this.$emit('input', pointsToEmit)
    },
    getTimeFromError (item) {
      if (!item.dirtTime) {
        return false
      }
      if (item.timeFrom > item.timeTo) {
        return true
      }
      return false
    },
    getTimeFromErrorMessage (item) {
      if (!item.dirtTime) {
        return []
      }
      if (item.timeFrom > item.timeTo) {
        return this.$t('validation.invalidRange')
      }
      return []
    },
    getTimeToError (item) {
      if (!item.dirtTime) {
        return false
      }
      if (item.timeFrom > item.timeTo) {
        return true
      }
      return false
    },
    getTimeToErrorMessage (item) {
      if (!item.dirtTime) {
        return []
      }
      if (item.timeFrom > item.timeTo) {
        return this.$t('validation.invalidRange')
      }
      return []
    },
    convertTimeSpanToMillis (timeSpan) {
      if (!timeSpan) return null
      // TimeSpan format: "HH:MM:SS" or with days "DD.HH:MM:SS"
      const parts = timeSpan.split(':')
      if (parts.length >= 3) {
        const hours = parseInt(parts[0])
        const minutes = parseInt(parts[1])
        return moment().startOf('day').add(hours, 'hours').add(minutes, 'minutes').valueOf()
      }
      return null
    },
    convertMillisToTimeSpan (millis) {
      if (!millis) return null
      const m = moment(millis)
      const hours = m.hours()
      const minutes = m.minutes()
      const seconds = m.seconds()
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
    },
    onTimeCheckboxChange (item) {
      if (!item.showTimeSliders) {
        item.timeFrom = null
        item.timeTo = null
        item.dirtTime = false
      }
    },
    reorderPointsByDate () {
      this.isReordering = true

      // Sortuj punkty chronologicznie po dacie
      const sortedByDate = [...this.points].sort((a, b) => {
        // Punkty bez daty idą na koniec
        if (!a.dateTime && !b.dateTime) return a.order - b.order
        if (!a.dateTime) return 1
        if (!b.dateTime) return -1

        const dateA = new Date(a.dateTime)
        const dateB = new Date(b.dateTime)

        // Sortuj po dacie
        if (dateA.getTime() !== dateB.getTime()) {
          return dateA - dateB
        }

        // Jeśli daty są takie same, zachowaj obecną kolejność
        return a.order - b.order
      })

      // Przypisz nowe wartości order
      sortedByDate.forEach((point, index) => {
        point.order = index
      })

      this.$nextTick(() => {
        this.isReordering = false
      })
    },
    switchType (item) {
      item.type = item.type === 0 ? 1 : 0
    },
    moveUp (item) {
      const sortedByOrder = [...this.points].sort((a, b) => a.order - b.order)
      const currentIndex = sortedByOrder.findIndex(p => p === item)

      if (currentIndex > 0) {
        const prevItem = sortedByOrder[currentIndex - 1]
        const tempOrder = item.order
        item.order = prevItem.order
        prevItem.order = tempOrder
      }
    },
    moveDown (item) {
      const sortedByOrder = [...this.points].sort((a, b) => a.order - b.order)
      const currentIndex = sortedByOrder.findIndex(p => p === item)

      if (currentIndex < sortedByOrder.length - 1) {
        const nextItem = sortedByOrder[currentIndex + 1]
        const tempOrder = item.order
        item.order = nextItem.order
        nextItem.order = tempOrder
      }
    },
    canMoveUp (item) {
      const sortedByOrder = [...this.points].sort((a, b) => a.order - b.order)
      const currentIndex = sortedByOrder.findIndex(p => p === item)

      if (currentIndex <= 0) {
        return false
      }

      const prevItem = sortedByOrder[currentIndex - 1]
      if (prevItem.dateTime !== null && item.dateTime !== null && prevItem.dateTime === item.dateTime) {
        return true
      }

      return false
    },
    canMoveDown (item) {
      const sortedByOrder = [...this.points].sort((a, b) => a.order - b.order)
      const currentIndex = sortedByOrder.findIndex(p => p === item)

      if (currentIndex >= sortedByOrder.length - 1) {
        return false
      }

      const nextItem = sortedByOrder[currentIndex + 1]
      if (nextItem.dateTime !== null && item.dateTime !== null && nextItem.dateTime === item.dateTime) {
        return true
      }

      return false
    },
    deletePoint (item) {
      const index = this.points.indexOf(item)
      this.points.splice(index, 1)
    },
    addPoint () {
      const maxOrder = this.points.length > 0
        ? Math.max(...this.points.map(p => p.order))
        : -1

      this.points.push({
        location: {
          country: null,
          place: {}
        },
        type: 0,
        order: maxOrder + 1,
        dateTime: null,
        description: null,
        showTimeSliders: false,
        timeFrom: null,
        timeTo: null,
        fix: null,
        weight: null,
        loadMeters: null,
        dirtTime: false
      })
    }
  }
}
</script>

<style scoped>
.v-messages {
  text-align: left !important;
}
</style>
